import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Eye, Trash2, LogOut, RefreshCw, FileText, Mail, BookOpen, Brain, ClipboardList, X, FileDown } from "lucide-react";
import { generateStrategyProfilePdf } from "./strategyProfilePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ldr_admin_token";

const VIEWABLE_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
const STATUSES = ["new", "reviewing", "replied", "scheduled", "closed"];
const STATUS_LABEL = { new: "New", reviewing: "Reviewing", replied: "Replied", scheduled: "Scheduled", closed: "Closed" };
const STATUS_COLOR = {
  new: "bg-camel text-nearblack",
  reviewing: "bg-camellight/40 text-nearblack",
  replied: "bg-paperdeep text-nearblack",
  scheduled: "bg-paperdeep text-nearblack",
  closed: "bg-stonegrey/30 text-nearblack/60",
};

// Strategy Profile uses its own lightweight triage flow
const SP_STATUSES = ["new", "reviewed", "called"];
const SP_STATUS_LABEL = { new: "New", reviewed: "Reviewed", called: "Called" };
const SP_STATUS_COLOR = {
  new: "bg-camel text-nearblack",
  reviewed: "bg-camellight/40 text-nearblack",
  called: "bg-stonegrey/30 text-nearblack/60",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const filenameFromHeader = (disp) => {
  if (!disp) return null;
  const s = /filename\*=UTF-8''([^;]+)/i.exec(disp);
  if (s) return decodeURIComponent(s[1]);
  const b = /filename="([^"]+)"/i.exec(disp);
  return b ? b[1] : null;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("policy");
  const [policies, setPolicies] = useState([]);
  const [subs, setSubs] = useState([]);
  const [guides, setGuides] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const tryAuth = useCallback(async (tok) => {
    if (!tok) return false;
    try { await axios.get(`${API}/admin/login`, { headers: { Authorization: `Bearer ${tok}` } }); return true; }
    catch { return false; }
  }, []);

  const refresh = useCallback(async (tok) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tok || token}` };
      const [pr, ns, fg, qz, sp] = await Promise.all([
        axios.get(`${API}/admin/policy-reviews`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/free-guide`, { headers }),
        axios.get(`${API}/admin/quiz`, { headers }),
        axios.get(`${API}/admin/strategy-profile`, { headers }),
      ]);
      setPolicies(pr.data || []);
      setSubs(ns.data || []);
      setGuides(fg.data || []);
      setQuizzes(qz.data || []);
      setStrategies(sp.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    (async () => {
      if (token) {
        const ok = await tryAuth(token);
        if (ok) { setAuthed(true); refresh(token); }
        else { localStorage.removeItem(STORAGE_KEY); setToken(""); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const tok = token.trim();
    if (!tok) return toast.error("Enter your token");
    const ok = await tryAuth(tok);
    if (ok) { localStorage.setItem(STORAGE_KEY, tok); setToken(tok); setAuthed(true); refresh(tok); }
    else toast.error("Invalid token. If your token has special characters, paste carefully without trailing spaces.");
  };

  const handleLogout = () => { localStorage.removeItem(STORAGE_KEY); setToken(""); setAuthed(false); };

  // Counts
  const newThisWeek = useMemo(() => {
    const wk = Date.now() - 7 * 24 * 3600 * 1000;
    return policies.filter((r) => (!r.status || r.status === "new") && new Date(r.created_at).getTime() > wk).length;
  }, [policies]);

  const filteredPolicies = useMemo(() => {
    if (statusFilter === "all") return policies;
    return policies.filter((r) => (r.status || "new") === statusFilter);
  }, [policies, statusFilter]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-nearblack text-paper flex items-center justify-center p-6">
        <form onSubmit={handleLogin} className="w-full max-w-md bg-nearblack border border-camel/40 p-10">
          <p className="eyebrow-light mb-5">Liz Dirocco · Admin</p>
          <h1 className="font-serif text-3xl text-paper mb-8"><em className="italic text-camellight">Private</em> access</h1>
          <label className="block text-[11px] tracking-[0.22em] uppercase text-paper/60 mb-2">Token</label>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Enter admin token" autoFocus
            className="w-full bg-transparent border-b border-paper/30 py-3 text-paper placeholder:text-paper/30 focus:outline-none focus:border-camel" />
          <button type="submit" className="mt-8 w-full btn-camel">Enter</button>
          <button type="button" onClick={() => { localStorage.removeItem(STORAGE_KEY); setToken(""); toast.success("Saved token cleared"); }}
            className="mt-4 w-full text-[11px] tracking-[0.22em] uppercase text-paper/60 hover:text-camellight py-3">Clear saved token</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-nearblack">
      <header className="bg-nearblack text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <p className="eyebrow-light mb-1">Liz Dirocco · Admin</p>
            <h1 className="font-serif text-2xl md:text-3xl"><em className="italic text-camellight">Submissions</em></h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => refresh()} disabled={loading} className="inline-flex items-center gap-2 border border-paper/30 text-paper hover:bg-paper/5 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 border border-paper/30 text-paper hover:bg-paper/5 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-paperdeep bg-paper sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-8 items-center">
          {[
            { id: "policy", label: `Policy Reviews · ${policies.length}`, icon: FileText },
            { id: "newsletter", label: `Newsletter · ${subs.length}`, icon: Mail },
            { id: "freeguide", label: `Free Guide · ${guides.length}`, icon: BookOpen },
            { id: "quiz", label: `Quiz · ${quizzes.length}`, icon: Brain },
            { id: "strategy", label: `Strategy Profile · ${strategies.length}`, icon: ClipboardList },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} className={`inline-flex items-center gap-2 py-5 text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors ${tab === id ? "border-camel text-nearblack" : "border-transparent text-stonegrey hover:text-nearblack"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
          {tab === "policy" && newThisWeek > 0 && (
            <span className="ml-auto inline-flex items-center gap-2 bg-camel text-nearblack px-3 py-1.5 text-[10.5px] tracking-[0.22em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-nearblack animate-pulse" />
              {newThisWeek} new this week
            </span>
          )}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "policy" && (
          <PolicyTable
            rows={filteredPolicies}
            token={token}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onChanged={() => refresh()}
          />
        )}
        {tab === "newsletter" && <NewsletterTable rows={subs} token={token} onChanged={() => refresh()} />}
        {tab === "freeguide" && <FreeGuideTable rows={guides} token={token} onChanged={() => refresh()} />}
        {tab === "quiz" && <QuizTable rows={quizzes} token={token} onChanged={() => refresh()} />}
        {tab === "strategy" && <StrategyProfileTable rows={strategies} token={token} onChanged={() => refresh()} />}
      </main>
    </div>
  );
}

function PolicyTable({ rows, token, statusFilter, setStatusFilter, onChanged }) {
  const [busyId, setBusyId] = useState(null);

  const fetchBlob = async (row) => {
    const res = await axios.get(`${API}/admin/policy-review/file/${row.file_id}`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
    });
    if (res.data?.type?.includes("application/json")) {
      const text = await res.data.text();
      let msg = text; try { msg = JSON.parse(text).detail || text; } catch {}
      throw new Error(msg);
    }
    const filename = filenameFromHeader(res.headers?.["content-disposition"]) || row.file_name || `policy_${row.file_id}`;
    return { blob: res.data, filename };
  };

  const handleDownload = async (row) => {
    if (!row.file_id) return; setBusyId(`dl-${row.id}`);
    try {
      const { blob, filename } = await fetchBlob(row);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) { console.error(err); toast.error(`Download failed: ${err?.message || err}`); }
    finally { setBusyId(null); }
  };

  const handleView = async (row) => {
    if (!row.file_id) return;
    const type = (row.file_content_type || "").toLowerCase();
    if (!VIEWABLE_TYPES.includes(type)) { return handleDownload(row); }
    setBusyId(`vw-${row.id}`);
    try {
      const { blob } = await fetchBlob(row);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) toast.error("Pop-up blocked — allow pop-ups for this site");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { console.error(err); toast.error(`View failed: ${err?.message || err}`); }
    finally { setBusyId(null); }
  };

  const handleStatus = async (row, newStatus) => {
    setBusyId(`st-${row.id}`);
    try {
      await axios.patch(`${API}/admin/policy-review/${row.id}/status`,
        { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      onChanged();
    } catch (err) { toast.error("Failed to update status"); }
    finally { setBusyId(null); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this submission? This cannot be undone.")) return;
    setBusyId(`del-${row.id}`);
    try {
      await axios.delete(`${API}/admin/policy-review/${row.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Submission deleted");
      onChanged();
    } catch (err) { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] tracking-[0.22em] uppercase text-stonegrey mr-2">Filter</span>
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-[10.5px] tracking-[0.22em] uppercase border ${statusFilter === s ? "border-nearblack bg-nearblack text-paper" : "border-paperdeep text-nearblack/70 hover:border-nearblack"}`}>
              {s === "all" ? "All" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {!rows.length ? (
        <p className="text-stonegrey text-sm">No submissions match this filter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stonegrey border-b border-paperdeep">
                {["Status", "Name", "Email", "Phone", "Insurance Co.", "Note", "Submitted", "Document", ""].map((h) => (
                  <th key={h} className="py-3 pr-3 text-[10.5px] tracking-[0.22em] uppercase font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const status = r.status || "new";
                const isNew = status === "new";
                return (
                  <tr key={r.id} className={`border-b border-paperdeep/60 align-top ${isNew ? "bg-camel/[0.06]" : ""}`}>
                    <td className="py-4 pr-3">
                      <select value={status} onChange={(e) => handleStatus(r, e.target.value)} disabled={busyId === `st-${r.id}`}
                        className={`px-2.5 py-1.5 text-[10.5px] tracking-[0.18em] uppercase border-0 ${STATUS_COLOR[status]} focus:outline-none focus:ring-1 focus:ring-camel cursor-pointer`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                      </select>
                    </td>
                    <td className="py-4 pr-3"><p className="font-serif text-base">{r.first_name} {r.last_name}</p></td>
                    <td className="py-4 pr-3"><a href={`mailto:${r.email}`} className="hover:text-camel">{r.email}</a></td>
                    <td className="py-4 pr-3 text-nearblack/75">{r.phone || "—"}</td>
                    <td className="py-4 pr-3 text-nearblack/75">{r.insurance_company || "—"}</td>
                    <td className="py-4 pr-3 text-nearblack/75 max-w-[220px]">{r.note || <span className="text-stonegrey">—</span>}</td>
                    <td className="py-4 pr-3 text-stonegrey whitespace-nowrap text-[12.5px]">{fmtDate(r.created_at)}</td>
                    <td className="py-4 pr-3 whitespace-nowrap">
                      {r.file_id ? (
                        <div className="inline-flex gap-2">
                          <a
                            href={`${API}/admin/policy-review/file/${r.file_id}?token=${encodeURIComponent(token)}&inline=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`view-${r.id}`}
                            title="Open in a new tab (PDFs and images render inline; other types prompt to save)"
                            className="inline-flex items-center gap-1.5 border border-nearblack/40 px-2.5 py-1.5 text-[10.5px] tracking-[0.22em] uppercase hover:border-nearblack"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </a>
                          <button onClick={() => handleDownload(r)} disabled={busyId === `dl-${r.id}`} data-testid={`download-${r.id}`}
                            className="inline-flex items-center gap-1.5 border border-nearblack px-2.5 py-1.5 text-[10.5px] tracking-[0.22em] uppercase hover:bg-nearblack hover:text-paper disabled:opacity-50">
                            <Download className="w-3.5 h-3.5" /> Download
                          </button>
                        </div>
                      ) : <span className="text-stonegrey text-xs">No file</span>}
                    </td>
                    <td className="py-4 pr-3">
                      <button onClick={() => handleDelete(r)} disabled={busyId === `del-${r.id}`} data-testid={`delete-${r.id}`}
                        title="Delete submission" aria-label="Delete submission"
                        className="text-stonegrey hover:text-destructive p-2 disabled:opacity-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewsletterTable({ rows, token, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const exportUrl = `${API}/admin/newsletter/export.csv`;

  const handleExport = async () => {
    try {
      const res = await axios.get(exportUrl, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "newsletter_subscribers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this submission? This cannot be undone.")) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/admin/newsletter/${row.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscriber deleted"); onChanged();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button onClick={handleExport} className="inline-flex items-center gap-2 btn-camel">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {rows.length === 0 ? <p className="text-stonegrey text-sm">No newsletter subscribers yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stonegrey border-b border-paperdeep">
                {["First Name", "Email", "Source", "Subscribed", ""].map((h) => (
                  <th key={h} className="py-3 pr-4 text-[10.5px] tracking-[0.22em] uppercase font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-paperdeep/60">
                  <td className="py-4 pr-4 font-serif">{r.first_name}</td>
                  <td className="py-4 pr-4"><a href={`mailto:${r.email}`} className="hover:text-camel">{r.email}</a></td>
                  <td className="py-4 pr-4 text-nearblack/70 text-[12.5px]">{r.source}</td>
                  <td className="py-4 pr-4 text-stonegrey whitespace-nowrap text-[12.5px]">{fmtDate(r.created_at)}</td>
                  <td className="py-4 pr-4">
                    <button onClick={() => handleDelete(r)} disabled={busyId === r.id} data-testid={`news-delete-${r.id}`}
                      aria-label="Delete subscriber" className="text-stonegrey hover:text-destructive p-2 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function FreeGuideTable({ rows, token, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const exportUrl = `${API}/admin/free-guide/export.csv`;

  const handleExport = async () => {
    try {
      const res = await axios.get(exportUrl, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "free_guide_requests.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this request? This cannot be undone.")) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/admin/free-guide/${row.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Request deleted"); onChanged();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button onClick={handleExport} data-testid="freeguide-export" className="inline-flex items-center gap-2 btn-camel">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {rows.length === 0 ? <p className="text-stonegrey text-sm">No free guide requests yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stonegrey border-b border-paperdeep">
                {["First Name", "Email Address", "Submitted", ""].map((h) => (
                  <th key={h} className="py-3 pr-4 text-[10.5px] tracking-[0.22em] uppercase font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-paperdeep/60">
                  <td className="py-4 pr-4 font-serif">{r.first_name}</td>
                  <td className="py-4 pr-4"><a href={`mailto:${r.email}`} className="hover:text-camel">{r.email}</a></td>
                  <td className="py-4 pr-4 text-stonegrey whitespace-nowrap text-[12.5px]">{fmtDate(r.created_at)}</td>
                  <td className="py-4 pr-4">
                    <button onClick={() => handleDelete(r)} disabled={busyId === r.id} data-testid={`freeguide-delete-${r.id}`}
                      aria-label="Delete request" className="text-stonegrey hover:text-destructive p-2 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function QuizTable({ rows, token, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("all");
  const exportUrl = `${API}/admin/quiz/export.csv`;

  const filtered = filter === "all" ? rows : rows.filter((r) => r.dominant === filter);

  const handleExport = async () => {
    try {
      const res = await axios.get(exportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "quiz_submissions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this submission? This cannot be undone.")) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/admin/quiz/${row.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Submission deleted"); onChanged();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  const patternColors = {
    upgrader: "bg-camel/15 text-nearblack",
    exposed: "bg-camel/15 text-nearblack",
    performer: "bg-camel/15 text-nearblack",
    uninformed: "bg-camel/15 text-nearblack",
  };
  const patternLabel = {
    upgrader: "The Upgrader",
    exposed: "The Exposed",
    performer: "The Performer",
    uninformed: "The Uninformed",
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: `All · ${rows.length}` },
            { id: "upgrader", label: "Upgrader" },
            { id: "exposed", label: "Exposed" },
            { id: "performer", label: "Performer" },
            { id: "uninformed", label: "Uninformed" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`quiz-filter-${f.id}`}
              className={`px-3 py-2 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                filter === f.id
                  ? "bg-nearblack text-paper border-nearblack"
                  : "bg-paper text-stonegrey border-paperdeep hover:border-camel"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={handleExport} data-testid="quiz-export" className="inline-flex items-center gap-2 btn-camel">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {filtered.length === 0 ? (
        <p className="text-stonegrey text-sm">No quiz submissions {filter !== "all" ? `for ${filter}` : ""} yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stonegrey border-b border-paperdeep">
                {["Name", "Email", "Dominant", "Lifestyle", "Security", "Identity", "Knowledge", "Submitted", ""].map((h) => (
                  <th key={h} className="py-3 pr-4 text-[10.5px] tracking-[0.22em] uppercase font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-paperdeep/60">
                  <td className="py-4 pr-4 font-serif">{r.first_name}</td>
                  <td className="py-4 pr-4"><a href={`mailto:${r.email}`} className="hover:text-camel">{r.email}</a></td>
                  <td className="py-4 pr-4">
                    <span className={`inline-block px-3 py-1 text-[11px] uppercase tracking-[0.18em] ${patternColors[r.dominant] || "bg-paper"}`}>
                      {patternLabel[r.dominant] || r.dominant}
                    </span>
                  </td>
                  <td className="py-4 pr-4 text-stonegrey text-[12.5px]">{r.section1_score}/16 · {r.section1_label}</td>
                  <td className="py-4 pr-4 text-stonegrey text-[12.5px]">{r.section2_score}/16 · {r.section2_label}</td>
                  <td className="py-4 pr-4 text-stonegrey text-[12.5px]">{r.section3_score}/16 · {r.section3_label}</td>
                  <td className="py-4 pr-4 text-stonegrey text-[12.5px]">{r.section4_score}/16 · {r.section4_label}</td>
                  <td className="py-4 pr-4 text-stonegrey whitespace-nowrap text-[12.5px]">{fmtDate(r.created_at)}</td>
                  <td className="py-4 pr-4">
                    <button onClick={() => handleDelete(r)} disabled={busyId === r.id} data-testid={`quiz-delete-${r.id}`}
                      aria-label="Delete submission" className="text-stonegrey hover:text-destructive p-2 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Strategy Profile (long-form pre-call intake)
// ────────────────────────────────────────────────────────────────────

const STRATEGY_SECTIONS = [
  {
    title: "01 · About You",
    fields: [
      ["first_name", "First name"], ["last_name", "Last name"],
      ["email", "Email"], ["phone", "Phone"],
      ["dob", "Date of birth"], ["state", "State of residence"],
      ["marital_status", "Marital status"], ["decision_maker", "Decision maker"],
      ["employment", "Employment"], ["employment_other", "Employment (other)"],
      ["industry", "Industry"], ["household", "Household structure"],
      ["children", "Children?"], ["grandchildren", "Grandchildren?"],
      ["dependent_ages", "Dependent ages"],
      ["housing", "Housing"], ["other_real_estate", "Other real estate"],
    ],
  },
  {
    title: "02 · Family, Vision & Values",
    fields: [
      ["family_context", "Family context"], ["money_goal", "Money goals"],
      ["rich_life", "Rich life"], ["financial_upbringing", "Financial upbringing"],
      ["legacy_intent", "Legacy intent"], ["generational_hopes", "Generational hopes"],
      ["motivation", "Motivation"], ["long_term_role", "Long-term role"],
    ],
  },
  {
    title: "03 · Income & Cash Flow",
    fields: [
      ["income_sources", "Income sources"], ["primary_income", "Primary annual income"],
      ["additional_income", "Additional income"], ["income_consistency", "Income consistency"],
      ["retirement_contribution", "Retirement contribution"], ["outside_savings", "Outside savings"],
      ["tax_filing", "Tax filing"], ["cpa", "CPA"], ["income_outlook", "Income outlook"],
    ],
  },
  {
    title: "04 · Cash Flow & Money Habits",
    fields: [
      ["monthly_takehome", "Monthly take-home"], ["monthly_spend", "Monthly spend"],
      ["money_flow_first", "Money flow first"], ["money_control_feeling", "Money control feeling"],
      ["month_end", "End of month"], ["cashflow_clarity", "Cashflow clarity"],
      ["budgeting", "Budgeting"], ["redirect_percent", "Redirect %"],
      ["game_changing_habit", "Game-changing habit"], ["future_expenses", "Future expenses"],
    ],
  },
  {
    title: "05 · Assets & Net Worth",
    fields: [
      ["assets", "Assets"], ["liquid_savings", "Liquid savings"],
      ["emergency_fund", "Emergency fund"], ["retirement_total", "Retirement total"],
    ],
  },
  {
    title: "06 · Liabilities & Debt",
    fields: [
      ["liabilities", "Liabilities"], ["total_debt", "Total debt"],
      ["money_mindset", "Money mindset"],
    ],
  },
  {
    title: "07 · Goals & Priorities",
    fields: [
      ["top_12mo_goal", "Top 12-mo goal"], ["sixmo_milestone", "6-mo milestone"],
      ["three_year_vision", "3-year vision"], ["goals", "Goals"],
      ["life_events", "Life events"], ["biggest_obstacle", "Biggest obstacle"],
      ["ibc_response", "IBC reaction"],
    ],
  },
  {
    title: "08 · Protection & Insurance",
    fields: [
      ["insurance", "Insurance"], ["hlv_insured", "Human life value insured"],
      ["child_insurance", "Child insurance"], ["income_gap_coverage", "Income gap coverage"],
      ["ltc_plan", "Long-term care plan"], ["risk_tolerance", "Risk tolerance"],
      ["banking_frustration", "Banking frustration"],
    ],
  },
  {
    title: "09 · Health & Lifestyle",
    fields: [
      ["height", "Height"], ["weight", "Weight"],
      ["tobacco", "Tobacco"], ["vape", "Vape"], ["marijuana", "Marijuana"],
      ["prescriptions", "Prescriptions"], ["prescriptions_detail", "Prescriptions detail"],
      ["pending_exams", "Pending exams"], ["pending_exams_detail", "Pending exams detail"],
      ["pending_results", "Pending results"], ["pending_results_detail", "Pending results detail"],
      ["insurability", "Insurability concern"], ["insurability_detail", "Insurability detail"],
    ],
  },
  {
    title: "10 · Business, Estate & Legal",
    fields: [
      ["business_owner", "Business owner"], ["exit_plan", "Exit plan"],
      ["buy_sell", "Buy-sell / key person"], ["will_trust", "Will / trust"],
      ["beneficiaries", "Beneficiaries"],
    ],
  },
  {
    title: "11 · Mindset & Final Thoughts",
    fields: [
      ["financial_literacy", "Financial literacy (1-5)"], ["concepts", "Familiar concepts"],
      ["prior_advisor", "Prior advisor"], ["bad_experience", "Bad experience"],
      ["commitment", "Commitment (1-5)"], ["trigger_moment", "Trigger moment"],
      ["most_important", "Most important"], ["call_success", "Call success"],
      ["referral_source", "Referral source"], ["anything_else", "Anything else"],
    ],
  },
];

function renderValue(v) {
  if (v === undefined || v === null || v === "") return <span className="text-stonegrey/60 italic">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-stonegrey/60 italic">—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {v.map((x, i) => (
          <span key={i} className="inline-block bg-paperdeep/60 px-2 py-1 text-[11.5px]">{String(x)}</span>
        ))}
      </div>
    );
  }
  return <span className="whitespace-pre-wrap break-words">{String(v)}</span>;
}

function StrategyProfileTable({ rows, token, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const exportUrl = `${API}/admin/strategy-profile/export.csv`;

  const handleExport = async () => {
    try {
      const res = await axios.get(exportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "strategy_profiles.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleStatus = async (row, newStatus) => {
    if ((row.status || "new") === newStatus) return;
    setBusyId(`st-${row.id}`);
    try {
      await axios.patch(`${API}/admin/strategy-profile/${row.id}/status`,
        { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked ${SP_STATUS_LABEL[newStatus]}`);
      onChanged();
    } catch { toast.error("Failed to update status"); }
    finally { setBusyId(null); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this Strategy Profile submission? This cannot be undone.")) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/admin/strategy-profile/${row.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Submission deleted");
      if (viewRow && viewRow.id === row.id) setViewRow(null);
      onChanged();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  const filteredRows = statusFilter === "all"
    ? rows
    : rows.filter((r) => (r.status || "new") === statusFilter);

  const statusCounts = rows.reduce((acc, r) => {
    const s = r.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <p className="text-stonegrey text-sm">
          {rows.length === 0
            ? "No Strategy Profile submissions yet."
            : `${filteredRows.length} of ${rows.length} · sorted newest first`}
        </p>
        <button onClick={handleExport} data-testid="strategy-export" className="inline-flex items-center gap-2 btn-camel">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="strategy-status-filter">
          {["all", ...SP_STATUSES].map((s) => {
            const count = s === "all" ? rows.length : (statusCounts[s] || 0);
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                data-testid={`strategy-filter-${s}`}
                className={`px-3 py-1.5 text-[10.5px] tracking-[0.22em] uppercase border ${
                  statusFilter === s
                    ? "border-nearblack bg-nearblack text-paper"
                    : "border-paperdeep text-nearblack/70 hover:border-nearblack"
                }`}
              >
                {s === "all" ? "All" : SP_STATUS_LABEL[s]} · {count}
              </button>
            );
          })}
        </div>
      )}

      {rows.length === 0 ? null : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="strategy-table">
            <thead>
              <tr className="text-left text-stonegrey border-b border-paperdeep">
                {["Status", "Name", "Email", "Phone", "State", "Submitted", ""].map((h) => (
                  <th key={h} className="py-3 pr-4 text-[10.5px] tracking-[0.22em] uppercase font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const fullName = [r.first_name, r.last_name].filter(Boolean).join(" ") || r.first_name || "—";
                const status = r.status || "new";
                return (
                  <tr key={r.id} className="border-b border-paperdeep/60">
                    <td className="py-4 pr-4">
                      <select
                        value={status}
                        onChange={(e) => handleStatus(r, e.target.value)}
                        disabled={busyId === `st-${r.id}`}
                        data-testid={`strategy-status-${r.id}`}
                        className={`px-2.5 py-1.5 text-[10.5px] tracking-[0.18em] uppercase border-0 ${SP_STATUS_COLOR[status]} focus:outline-none focus:ring-1 focus:ring-camel cursor-pointer`}
                      >
                        {SP_STATUSES.map((s) => (
                          <option key={s} value={s}>{SP_STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-4 pr-4 font-serif">{fullName}</td>
                    <td className="py-4 pr-4"><a href={`mailto:${r.email}`} className="hover:text-camel">{r.email}</a></td>
                    <td className="py-4 pr-4 text-stonegrey">
                      {r.phone ? <a href={`tel:${r.phone}`} className="hover:text-camel">{r.phone}</a> : <span className="text-stonegrey/60">—</span>}
                    </td>
                    <td className="py-4 pr-4 text-stonegrey text-[12.5px]">{r.state || <span className="text-stonegrey/60">—</span>}</td>
                    <td className="py-4 pr-4 text-stonegrey whitespace-nowrap text-[12.5px]">{fmtDate(r.created_at)}</td>
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewRow(r)} data-testid={`strategy-view-${r.id}`}
                          aria-label="View full submission" className="text-stonegrey hover:text-camel p-2">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(r)} disabled={busyId === r.id} data-testid={`strategy-delete-${r.id}`}
                          aria-label="Delete submission" className="text-stonegrey hover:text-destructive p-2 disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewRow && (
        <StrategyProfileDrawer row={viewRow} onClose={() => setViewRow(null)} />
      )}
    </div>
  );
}

function StrategyProfileDrawer({ row, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const fullName = [row.first_name, row.last_name].filter(Boolean).join(" ") || row.first_name || "—";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="strategy-detail-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-nearblack/60" />
      <div
        className="relative w-full max-w-3xl h-full bg-paper overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-nearblack text-paper px-8 py-6 flex items-start justify-between z-10">
          <div>
            <p className="eyebrow-light mb-2">Strategy Profile · Confidential</p>
            <h2 className="font-serif text-3xl"><em className="italic text-camellight">{fullName}</em></h2>
            <p className="text-paper/60 text-[12.5px] mt-2 tracking-[0.05em]">
              {row.email}{row.phone ? ` · ${row.phone}` : ""}{row.state ? ` · ${row.state}` : ""}
            </p>
            <p className="text-paper/40 text-[11px] mt-1">Submitted {fmtDate(row.created_at)}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="strategy-detail-close"
            aria-label="Close"
            className="text-paper/70 hover:text-camellight p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Actions toolbar (just under sticky header) */}
        <div className="px-8 py-4 bg-paperdeep/40 border-b border-paperdeep flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              try {
                generateStrategyProfilePdf(row);
                toast.success("PDF downloaded");
              } catch (err) {
                console.error(err);
                toast.error("Could not generate PDF");
              }
            }}
            data-testid="strategy-detail-export-pdf"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium px-4 py-2.5 bg-nearblack text-paper hover:bg-[#2E2B27] transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" /> Export as PDF
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-8 space-y-10">
          {STRATEGY_SECTIONS.map((section) => {
            const hasAnyValue = section.fields.some(([key]) => {
              const v = row[key];
              return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
            });
            return (
              <section key={section.title}>
                <h3 className="text-[11px] tracking-[0.22em] uppercase text-camel mb-4 pb-2 border-b border-paperdeep">
                  {section.title}
                </h3>
                {!hasAnyValue ? (
                  <p className="text-stonegrey/70 italic text-sm">No answers in this section.</p>
                ) : (
                  <dl className="space-y-4">
                    {section.fields.map(([key, label]) => {
                      const v = row[key];
                      const isEmpty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
                      if (isEmpty) return null;
                      return (
                        <div key={key} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-1 md:gap-6 items-start">
                          <dt className="text-[11px] tracking-[0.18em] uppercase text-stonegrey pt-0.5">{label}</dt>
                          <dd className="text-nearblack text-[14px] leading-relaxed">{renderValue(v)}</dd>
                        </div>
                      );
                    })}
                  </dl>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
